import { JSONSchema7 } from 'json-schema';
import { useContext, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import {
  WorkflowEngineContext,
  WorkflowEngineMode
} from '../../WorkflowEngine/workflowEngineReducer';
import ControlReadOnlyTextPlaceholder from './ControlReadOnlyTextPlaceholder';
import { decodeTextWithEventData } from './utils';
import ReadOnlyRichTextEditor from './RichTextEditor/ReadOnlyRichTextEditor';
import Stack from '@mui/material/Stack';
import { ControlEntryState } from '../../FormEngine/Entry/controlEntryState';
import isJson from '../../Shared/isJson';
import isIE11 from '../../Shared/isIE11';

interface Block {
  text: string;
  type: string;
}

export default (control: ControlEntryState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { mode, eventData } = state;
  const isBuilder = mode === WorkflowEngineMode.BUILD;
  const properties = control.schema?.properties as JSONSchema7Properties;

  const labelSchema = properties['label'] as JSONSchema7;
  const defaultValue = labelSchema.default as string;
  const stylingSchema = properties['styling'] as JSONSchema7;

  const [text, setText] = useState<string>('');
  const [hasEventDataTemplate, setHasEventDataTemplate] = useState(true);
  const controlWrapperWidth: number = control?.wrapperSize?.width || NaN;

  const alertWrapperSpacer = 32;

  const renderView = () => {
    if (!text) return null;

    if (isIE11()) {
      if (isJson(text)) {
        const textObject = JSON.parse(text);
        const blocks = textObject.blocks as Block[];
        const textBlockElements = [];

        for (let i = 0; i < blocks.length; i++) {
          const currentBlock = blocks[i];
          switch (currentBlock.type) {
            case 'unordered-list-item': {
              const bulletedListElements = [];
              for (let j = i; j < blocks.length; j++) {
                const newCurrentBlock = blocks[j];
                if (newCurrentBlock.type === 'unordered-list-item') {
                  bulletedListElements.push(<li>{newCurrentBlock.text}</li>);
                } else {
                  break;
                }
                i = j;
              }

              textBlockElements.push(<ul>{bulletedListElements}</ul>);
              break;
            }
            case 'ordered-list-item': {
              const bulletedListElements = [];
              for (let j = i; j < blocks.length; j++) {
                const newCurrentBlock = blocks[j];
                if (newCurrentBlock.type === 'ordered-list-item') {
                  bulletedListElements.push(<li>{newCurrentBlock.text}</li>);
                } else {
                  break;
                }
                i = j;
              }

              textBlockElements.push(<ol>{bulletedListElements}</ol>);
              break;
            }
            default:
              textBlockElements.push(
                <div key={currentBlock.text}>{currentBlock.text}</div>
              );
          }
        }

        return textBlockElements;
      }
    }

    switch (stylingSchema.default) {
      case 'none':
        return (
          <ReadOnlyRichTextEditor
            width={isBuilder ? controlWrapperWidth : controlWrapperWidth}
            initialValue={text}
            showPlaceholder={isBuilder}
          />
        );

      case 'info':
        return (
          <Alert
            severity="info"
            sx={{
              width:
                controlWrapperWidth -
                (isBuilder ? alertWrapperSpacer : alertWrapperSpacer)
            }}
          >
            <ReadOnlyRichTextEditor
              width="100%"
              initialValue={text}
              showPlaceholder={isBuilder}
            />
          </Alert>
        );

      case 'warn':
        return (
          <Alert
            severity="warning"
            sx={{
              width:
                controlWrapperWidth -
                (isBuilder ? alertWrapperSpacer : alertWrapperSpacer)
            }}
          >
            <ReadOnlyRichTextEditor
              width="100%"
              initialValue={text}
              showPlaceholder={isBuilder}
            />
          </Alert>
        );

      default:
        return (
          <ReadOnlyRichTextEditor
            width={controlWrapperWidth}
            initialValue={text}
            showPlaceholder={isBuilder}
          />
        );
    }
  };

  const loadDefaultValue = (value: string) => {
    if (!isJson(value)) {
      setHasEventDataTemplate(false);
      setText('');
      return;
    }

    const draftJsObject = JSON.parse(value);
    const contentBlocks = draftJsObject.blocks as Block[];

    contentBlocks.forEach((block) => {
      const innerText = block.text;
      if (
        mode === WorkflowEngineMode.ENTRY &&
        /{*}/g.test(innerText) &&
        eventData
      ) {
        block.text = decodeTextWithEventData(innerText, eventData);
      }
    });

    setHasEventDataTemplate(false);

    draftJsObject.blocks = contentBlocks;

    if (
      draftJsObject.blocks.length === 1 &&
      draftJsObject.blocks[0].text === ''
    ) {
      setText('');
    } else {
      setText(JSON.stringify(draftJsObject));
    }
  };

  useEffect(() => {
    if (defaultValue) {
      loadDefaultValue(defaultValue);
    }
  }, [eventData]);

  return mode === WorkflowEngineMode.ENTRY && hasEventDataTemplate ? (
    <ControlReadOnlyTextPlaceholder />
  ) : (
    <Stack
      sx={{
        pointerEvents: mode === WorkflowEngineMode.BUILD ? 'none' : 'auto'
      }}
    >
      {renderView()}
    </Stack>
  );
};
